/**
 * The Clinical Intelligence Chamber's architecture (2026-10-05, the user's brief): one
 * sculpted space in warm graphite, stone, satin titanium, champagne aluminium and bronze,
 * lit from seams in the architecture itself, under a ceiling of suspended concentric rings
 * with slow neural light. See docs/chamber.md.
 *
 * Large surfaces (floor, walls, ceiling, the dashboard backdrop) are cheap unlit shaders that
 * paint their own soft light: fill rate is the Quest's limit (the lobby taught that a lit
 * floor sampling an environment cost 40 % of a frame). Small metal pieces are lit and
 * reflect a warm environment generated once per room.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { DOOR_HALF, HALL, type Point } from './plan.ts';

export const CHAMBER = {
  halfWidth: HALL.halfWidth,
  /** The entrance wall's z. */
  halfDepth: HALL.front,
  /** The top of the north wall's circle. */
  back: HALL.back,
  height: 6,
  /** The door's half width in the entrance wall (z = +halfDepth). */
  doorHalf: DOOR_HALF,
};

/** Shared time for every animated chamber shader. */
export interface ChamberTime {
  value: number;
}

/**
 * A floor or ceiling plan from an outline in (x, z), closed back to its first point. The
 * shape lies in x, -z so that rotating it about x puts it on the floor (or, turned the other
 * way, on the ceiling) at the right place.
 */
export function planShape(pts: Point[]): THREE.Shape {
  const shape = new THREE.Shape();
  shape.moveTo(pts[0]!.x, -pts[0]!.z);
  for (const p of pts.slice(1)) shape.lineTo(p.x, -p.z);
  shape.closePath();
  return shape;
}

const vertexUv = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorld;
  void main() {
    vUv = uv;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }`;

/**
 * The walls: soft architectural fabric in warm graphite, rising from a bronze-lit skirting to
 * a champagne cove, with vertical seams of soft cyan light every 2.4 m along which a pulse
 * rises slowly. Built as strips along each run of wall from its `bottom` (default the floor)
 * to its `top` (default 6 m); uv.x is metres along the wall, uv.y the height as a share of the
 * run's top, so a taller wall carries its cove up with it and a riser above an arch shows only
 * the upper fabric and the cove.
 */
export interface WallRun {
  points: Point[];
  bottom?: number;
  top?: number;
}

export function buildWalls(time: ChamberTime, accent: THREE.Color, runs: WallRun[]): THREE.Mesh {
  const positions: number[] = [];
  const uvs: number[] = [];
  const index: number[] = [];
  for (const run of runs) {
    const pts = run.points;
    const bottom = run.bottom ?? 0;
    const top = run.top ?? CHAMBER.height;
    const v0 = bottom / top;
    let s = 0;
    const base = positions.length / 3;
    pts.forEach((p, i) => {
      if (i > 0) s += Math.hypot(p.x - pts[i - 1]!.x, p.z - pts[i - 1]!.z);
      positions.push(p.x, bottom, p.z, p.x, top, p.z);
      uvs.push(s, v0, s, 1);
      if (i > 0) {
        const a = base + (i - 1) * 2;
        index.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
      }
    });
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(index);
  const material = new THREE.ShaderMaterial({
    uniforms: { uTime: time, uAccent: { value: accent }, uGlow: { value: 1 } },
    vertexShader: vertexUv,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uAccent;
      uniform float uGlow;
      varying vec2 vUv;
      void main() {
        float y = vUv.y;
        float s = vUv.x;
        // Warm graphite fabric, lighter through the middle, deepening toward the ceiling.
        vec3 low = vec3(0.40, 0.365, 0.33);
        vec3 mid = vec3(0.56, 0.51, 0.455);
        vec3 c = mix(low, mid, clamp(y * 2.4, 0.0, 1.0));
        c *= 1.0 - 0.32 * clamp((y - 0.55) * 2.2, 0.0, 1.0);
        // A fine vertical weave.
        c *= 0.97 + 0.03 * abs(fract(s * 22.0) - 0.5) * 2.0;
        // Seams every 2.4 m: a thin line and a soft halo, with a pulse rising through each.
        float k = s / 2.4;
        float d = abs(fract(k) - 0.5) * 2.4;
        float seam = max(0.0, 1.0 - d * 90.0) + max(0.0, 1.0 - d * 6.0) * 0.12;
        float id = floor(k);
        float rise = fract(y * 0.9 - uTime * 0.035 - fract(id * 0.618) );
        float pulse = rise * rise * rise * rise;
        float fade = clamp(y * 8.0, 0.0, 1.0) * clamp((0.93 - y) * 8.0, 0.0, 1.0);
        c += uAccent * seam * fade * (0.35 + 0.9 * pulse) * uGlow;
        // Champagne cove under the ceiling, a bronze glow along the skirting.
        float cove = max(0.0, 1.0 - abs(y - 0.94) * 30.0);
        c += vec3(0.95, 0.80, 0.58) * cove * 0.55 * uGlow;
        float skirt = max(0.0, 1.0 - y * 45.0);
        c += vec3(0.78, 0.58, 0.36) * skirt * 0.45 * uGlow;
        gl_FragColor = vec4(c, 1.0);
      }`,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
  const mesh = new THREE.Mesh(g, material);
  mesh.name = 'chamber-walls';
  return mesh;
}

/**
 * The floor: warm stone composite with a faint mottling and grain, darkening softly toward
 * the walls, champagne inlay rings round the table and a pool of light under it. The pattern
 * comes from world position, so every outline in `plans` joins seamlessly.
 */
export function buildFloor(
  centre: THREE.Vector3,
  plans: Point[][],
  /** The dashboards' curve: the outer inlay follows it (the table moved off its centre). */
  arc: THREE.Vector3 = centre,
): THREE.Mesh {
  const g = new THREE.ShapeGeometry(plans.map(planShape), 6);
  g.rotateX(-Math.PI / 2);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uCentre: { value: new THREE.Vector2(centre.x, centre.z) },
      uArc: { value: new THREE.Vector2(arc.x, arc.z) },
    },
    vertexShader: vertexUv,
    fragmentShader: /* glsl */ `
      uniform vec2 uCentre;
      uniform vec2 uArc;
      varying vec3 vWorld;
      float ring(float r, float at, float w) { return max(0.0, 1.0 - abs(r - at) / w); }
      void main() {
        vec2 p = vWorld.xz;
        float r = length(p - uCentre);
        vec3 stone = vec3(0.47, 0.445, 0.42);
        // Mottling: two slow waves; grain: a hashed cell pattern.
        float mott = sin(p.x * 0.9 + sin(p.y * 0.7)) * sin(p.y * 1.1 + p.x * 0.3);
        float grain = fract(sin(dot(floor(p * 9.0), vec2(12.99, 78.23))) * 437.5);
        vec3 c = stone * (0.95 + 0.04 * mott + 0.03 * grain);
        // Soft vignette toward the walls and a warm pool under the table.
        c *= 1.0 - 0.28 * clamp((r - 5.0) / 7.0, 0.0, 1.0);
        c += vec3(0.12, 0.10, 0.075) * max(0.0, 1.0 - r / 4.2);
        // A soft sheen, as if the floor were polished: brighter along the view's grazing line.
        c += vec3(0.05, 0.045, 0.04) * (0.5 + 0.5 * sin(p.x * 0.35 + p.y * 0.2));
        // Champagne inlays.
        vec3 champagne = vec3(0.78, 0.66, 0.47);
        float inlay = ring(r, 3.4, 0.03) + ring(length(p - uArc), 8.45, 0.025);
        c = mix(c, champagne, clamp(inlay, 0.0, 1.0) * 0.85);
        gl_FragColor = vec4(c, 1.0);
      }`,
    toneMapped: false,
  });
  const mesh = new THREE.Mesh(g, material);
  mesh.name = 'chamber-floor';
  return mesh;
}

/** A neural-and-molecular light pattern for the ceiling, drawn once. */
function neuralTexture(): THREE.CanvasTexture {
  const S = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const g = canvas.getContext('2d')!;
  g.fillStyle = '#000';
  g.fillRect(0, 0, S, S);
  let seed = 31;
  const rand = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const nodes: [number, number][] = [];
  for (let i = 0; i < 170; i++) {
    const a = rand() * Math.PI * 2;
    const r = Math.sqrt(rand()) * S * 0.48;
    nodes.push([S / 2 + Math.cos(a) * r, S / 2 + Math.sin(a) * r]);
  }
  g.lineCap = 'round';
  for (const [x, y] of nodes) {
    // Link to the two nearest neighbours with gently curved paths.
    const near = nodes
      .map((n) => [n, (n[0] - x) ** 2 + (n[1] - y) ** 2] as const)
      .sort((a, b) => a[1] - b[1])
      .slice(1, 3);
    for (const [[nx, ny]] of near) {
      g.strokeStyle = 'rgba(255,255,255,0.55)';
      g.lineWidth = 1.6;
      g.beginPath();
      g.moveTo(x, y);
      g.quadraticCurveTo(
        (x + nx) / 2 + (rand() - 0.5) * 40,
        (y + ny) / 2 + (rand() - 0.5) * 40,
        nx,
        ny,
      );
      g.stroke();
    }
  }
  for (const [x, y] of nodes) {
    const grad = g.createRadialGradient(x, y, 0, x, y, 9);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.beginPath();
    g.arc(x, y, 9, 0, Math.PI * 2);
    g.fill();
  }
  const t = new THREE.CanvasTexture(canvas);
  t.colorSpace = THREE.NoColorSpace;
  return t;
}

export interface Ceiling {
  group: THREE.Group;
  update(dt: number): void;
}

/**
 * The ceiling: a warm graphite soffit carrying a slow neural light pattern (pulses travel
 * outward along it), and four sculpted rings of champagne aluminium suspended in tiers
 * above the table, each lit from beneath by a line of soft cyan. The rings breathe with a
 * tilt of under a degree, each at its own pace. `plans` are outlines with their ceiling
 * heights (the hall at 6 m, the ward room higher when its tiers need it).
 */
export function buildCeiling(
  time: ChamberTime,
  centre: THREE.Vector3,
  accent: THREE.Color,
  metal: THREE.Material,
  plans: { outline: Point[]; height: number }[],
): Ceiling {
  const group = new THREE.Group();
  group.name = 'chamber-ceiling';
  const soffit = new THREE.Mesh(
    // Turned face-down, a plan shape lands mirrored in z; mirror the outline first so the
    // ceiling lies over the floor it belongs to.
    mergeGeometries(
      plans.map((p) =>
        new THREE.ShapeGeometry(planShape(p.outline.map((q) => ({ x: q.x, z: -q.z }))), 6)
          .rotateX(Math.PI / 2)
          .translate(0, p.height, 0),
      ),
    )!,
    new THREE.ShaderMaterial({
      uniforms: {
        uTime: time,
        uMap: { value: neuralTexture() },
        uCentre: { value: new THREE.Vector2(centre.x, centre.z) },
        uAccent: { value: accent },
      },
      vertexShader: vertexUv,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform sampler2D uMap;
        uniform vec2 uCentre;
        uniform vec3 uAccent;
        varying vec3 vWorld;
        void main() {
          vec2 d = vWorld.xz - uCentre;
          float r = length(d);
          vec3 c = mix(vec3(0.50, 0.46, 0.415), vec3(0.36, 0.33, 0.30), clamp(r / 11.0, 0.0, 1.0));
          // The dome over the table: deep blue, a field of neural light, a warm rim.
          float dome = 1.0 - smoothstep(3.2, 3.35, r);
          float n = texture2D(uMap, d / 7.0 + 0.5).r;
          float wave = fract(r * 0.25 - uTime * 0.05);
          float pulse = wave * wave * wave * wave * wave * wave;
          vec3 domeC = mix(vec3(0.10, 0.20, 0.28), vec3(0.04, 0.10, 0.16), r / 3.3);
          domeC += vec3(0.45, 0.80, 0.95) * n * (0.35 + 0.8 * pulse);
          c = mix(c, domeC, dome);
          c += vec3(1.0, 0.86, 0.66) * max(0.0, 1.0 - abs(r - 3.3) * 14.0) * 0.9;
          // Faint pattern on the outer ceiling too.
          float n2 = texture2D(uMap, d / 18.0 + 0.5).r;
          c += uAccent * n2 * 0.05 * (1.0 - dome);
          gl_FragColor = vec4(c, 1.0);
        }`,
      toneMapped: false,
    }),
  );
  soffit.name = 'chamber-soffit';
  group.add(soffit);

  const glow = new THREE.MeshBasicMaterial({ color: accent, toneMapped: false });
  // Sized so the outermost ring clears the flat wall behind the centre screen, 7.3 m ahead of
  // the table since it moved 2 m toward it (2026-10-07; they were 2.2 to 7.9 m).
  const specs = [
    { r: 2.0, y: 5.0, tube: 0.11 },
    { r: 3.6, y: 5.3, tube: 0.13 },
    { r: 5.2, y: 5.55, tube: 0.15 },
    { r: 6.7, y: 5.75, tube: 0.16 },
  ];
  // The four rings are two meshes (metal and light, merged); the whole tiered sculpture
  // breathes with a tilt of under a degree, so the rings shift against each other slowly
  // through parallax rather than separate transforms (two draw calls instead of eight).
  const holder = new THREE.Group();
  holder.position.set(centre.x, 0, centre.z);
  const bodies = specs.map((spec) =>
    new THREE.TorusGeometry(spec.r, spec.tube, 12, 160)
      .rotateX(Math.PI / 2)
      .scale(1, 0.32, 1)
      .translate(0, spec.y, 0),
  );
  const lines = specs.map((spec) =>
    new THREE.TorusGeometry(spec.r, 0.012, 6, 200)
      .rotateX(Math.PI / 2)
      .translate(0, spec.y - spec.tube * 0.36, 0),
  );
  const body = new THREE.Mesh(mergeGeometries(bodies)!, metal);
  body.name = 'chamber-rings';
  const line = new THREE.Mesh(mergeGeometries(lines)!, glow);
  line.name = 'chamber-ring-lights';
  for (const g of [...bodies, ...lines]) g.dispose();
  holder.add(body, line);
  group.add(holder);

  return {
    group,
    update: () => {
      const t = time.value;
      holder.rotation.x = Math.sin(t * 0.07) * 0.01;
      holder.rotation.z = Math.cos(t * 0.053 + 1.3) * 0.01;
      holder.rotation.y = t * 0.004;
    },
  };
}

/**
 * The wall the dashboards are set into: a continuous curved surface of warm champagne
 * composite behind the arc, with a recessed alcove behind each dashboard whose edges glow
 * with warm light, so each screen reads as an illuminated opening in the architecture, not
 * a monitor on a stand. Built from segments of the arc (the two dashboard groups; the centre
 * screen has its own taller one), each with evenly pitched alcoves.
 */
export function buildBackdrop(
  time: ChamberTime,
  centre: THREE.Vector3,
  radius: number,
  /** Arcs of the circle, or with `straight` the chord between its two ends (a flat wall). */
  segments: { from: number; to: number; straight?: boolean }[],
  bottom: number,
  top: number,
  /**
   * One alcove per `pitch` (radians) from each segment's start, centred in its pitch;
   * `half` is the alcove's half width in pitches, v0..v1 its height as a share of the wall.
   * `metres` is the wall's length per pitch where it is not radius x pitch (a chord).
   */
  slot: { pitch: number; half: number; v0: number; v1: number; metres?: number },
): THREE.Mesh {
  const positions: number[] = [];
  const uvs: number[] = [];
  const index: number[] = [];
  for (const seg of segments) {
    const steps = Math.max(8, Math.round(((seg.to - seg.from) / slot.pitch) * 24));
    const base = positions.length / 3;
    const ax = centre.x + Math.cos(seg.from) * radius;
    const az = centre.z + Math.sin(seg.from) * radius;
    const bx = centre.x + Math.cos(seg.to) * radius;
    const bz = centre.z + Math.sin(seg.to) * radius;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const a = seg.from + (seg.to - seg.from) * t;
      const x = seg.straight ? ax + (bx - ax) * t : centre.x + Math.cos(a) * radius;
      const z = seg.straight ? az + (bz - az) * t : centre.z + Math.sin(a) * radius;
      // uv.x counts pitches from the segment's start, so alcove k is centred on k + 0.5.
      const u = (a - seg.from) / slot.pitch;
      positions.push(x, bottom, z, x, top, z);
      uvs.push(u, 0, u, 1);
      if (i > 0) {
        const k = base + (i - 1) * 2;
        index.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(index);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: time,
      uHalf: { value: slot.half },
      uV0: { value: slot.v0 },
      uV1: { value: slot.v1 },
      uGlow: { value: 1 },
      // Aspect: metres along the arc per uv.x (one pitch), and metres per uv.y.
      uScale: { value: new THREE.Vector2(slot.metres ?? radius * slot.pitch, top - bottom) },
    },
    vertexShader: vertexUv,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uHalf;
      uniform float uV0;
      uniform float uV1;
      uniform float uGlow;
      uniform vec2 uScale;
      varying vec2 vUv;
      void main() {
        // Warm champagne composite, a soft vertical sheen.
        vec3 c = mix(vec3(0.50, 0.455, 0.405), vec3(0.62, 0.565, 0.50), 1.0 - abs(vUv.y - 0.55) * 1.5);
        // The nearest alcove, in metres from its edge (negative inside).
        float cx = floor(vUv.x) + 0.5;
        float mx = 0.07;
        vec2 dm = vec2(abs(vUv.x - cx) - uHalf, max(uV0 - vUv.y, vUv.y - uV1)) * uScale;
        dm += vec2(-mx, -mx);
        float outside = max(dm.x, dm.y);
        // Inside: a deep recess, darker toward its centre (the screen sits in front of it).
        vec3 recess = vec3(0.16, 0.15, 0.14);
        c = mix(c, recess, step(outside, 0.0));
        // The alcove's lit edge: a warm line and a halo that spills onto the wall.
        float edge = max(0.0, 1.0 - abs(outside) * 70.0);
        float halo = max(0.0, 1.0 - max(outside, 0.0) * 6.0) * step(0.0, outside);
        c += vec3(1.0, 0.86, 0.64) * (edge * 1.1 + halo * 0.22) * uGlow;
        // Light along the top and foot of the whole wall.
        float band = max(0.0, 1.0 - (1.0 - vUv.y) * 60.0) + max(0.0, 1.0 - vUv.y * 60.0);
        c += vec3(1.0, 0.86, 0.64) * band * 0.7 * uGlow;
        gl_FragColor = vec4(c, 1.0);
      }`,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
  void time;
  const mesh = new THREE.Mesh(g, material);
  mesh.name = 'chamber-backdrop';
  return mesh;
}

/**
 * Two glass columns with a double helix of light turning slowly inside, standing either
 * side of the entrance. One draw call for both helices (points placed in the shader); the
 * glass and its bronze caps join the room's shell.
 */
export function buildHelixColumns(
  time: ChamberTime,
  spots: { x: number; z: number }[],
  height: number,
  base: number,
): THREE.Points {
  const perStrand = 70;
  const pos: number[] = [];
  const attr: number[] = [];
  spots.forEach((spot) => {
    for (let strand = 0; strand < 2; strand++)
      for (let i = 0; i < perStrand; i++) {
        pos.push(spot.x, base + (i / (perStrand - 1)) * height, spot.z);
        attr.push(i / (perStrand - 1), strand);
      }
    // Rungs: a few points between the strands.
    for (let i = 0; i < perStrand; i += 5)
      for (let j = 1; j < 4; j++) {
        pos.push(spot.x, base + (i / (perStrand - 1)) * height, spot.z);
        attr.push(i / (perStrand - 1), 2 + j / 4);
      }
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aHelix', new THREE.Float32BufferAttribute(attr, 2));
  g.computeBoundingSphere();
  const points = new THREE.Points(
    g,
    new THREE.ShaderMaterial({
      uniforms: { uTime: time, uHeight: { value: height } },
      vertexShader: /* glsl */ `
        uniform float uTime;
        uniform float uHeight;
        attribute vec2 aHelix;
        varying float vA;
        void main() {
          float t = aHelix.x;
          float strand = aHelix.y;
          float ang = t * 18.0 + uTime * 0.35;
          vec3 p = position;
          if (strand < 1.5) {
            float a = ang + strand * 3.14159;
            p.x += cos(a) * 0.11;
            p.z += sin(a) * 0.11;
            vA = 1.0;
          } else {
            float f = (strand - 2.0) * 2.0 - 1.0;
            p.x += cos(ang) * 0.11 * f;
            p.z += sin(ang) * 0.11 * f;
            vA = 0.5;
          }
          vA *= 0.55 + 0.45 * sin(t * 30.0 - uTime * 1.5);
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_PointSize = (strand < 1.5 ? 0.045 : 0.03) * 600.0 / -mv.z;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        varying float vA;
        void main() {
          vec2 q = gl_PointCoord - 0.5;
          float a = max(0.0, 1.0 - dot(q, q) * 4.0) * vA;
          gl_FragColor = vec4(vec3(0.6, 0.9, 1.0) * a, a);
        }`,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  points.name = 'chamber-helices';
  return points;
}

/**
 * Plants in brushed titanium planters: a living note in the architecture. Two draw calls.
 */
export function buildPlants(
  spots: { x: number; z: number; scale?: number }[],
  withPlanters = true,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'chamber-plants';
  const d = new THREE.Object3D();
  const planters = new THREE.InstancedMesh(
    new THREE.CylinderGeometry(0.26, 0.22, 0.5, 32).translate(0, 0.25, 0),
    new THREE.MeshPhongMaterial({ color: '#b9b3aa', specular: '#ffffff', shininess: 50 }),
    spots.length,
  );
  spots.forEach((s, i) => {
    d.position.set(s.x, 0, s.z);
    d.scale.setScalar(s.scale ?? 1);
    d.rotation.set(0, 0, 0);
    d.updateMatrix();
    planters.setMatrixAt(i, d.matrix);
  });
  let seed = 3;
  const rand = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const perPlant = 34;
  const leaves = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.06, 6, 4).scale(1, 3.2, 0.35).translate(0, 0.17, 0),
    new THREE.MeshLambertMaterial({ color: '#ffffff' }),
    spots.length * perPlant,
  );
  const c = new THREE.Color();
  spots.forEach((s, i) => {
    const k = s.scale ?? 1;
    for (let j = 0; j < perPlant; j++) {
      const a = rand() * Math.PI * 2;
      const r = rand() * 0.16 * k;
      d.position.set(s.x + Math.cos(a) * r, 0.48 * k + rand() * 0.55 * k, s.z + Math.sin(a) * r);
      d.rotation.set((rand() - 0.5) * 1.6, a, (rand() - 0.5) * 1.6);
      d.scale.setScalar((0.8 + rand() * 0.7) * k);
      d.updateMatrix();
      leaves.setMatrixAt(i * perPlant + j, d.matrix);
      leaves.setColorAt(
        i * perPlant + j,
        c.setHSL(0.27 + rand() * 0.06, 0.45, 0.28 + rand() * 0.12),
      );
    }
  });
  if (withPlanters) group.add(planters);
  group.add(leaves);
  return group;
}

/**
 * Light rising above the table: a soft column from the innermost ceiling ring down to the
 * table top, with slow streaks, and drifting motes of champagne light through the chamber.
 */
export function buildAtmosphere(
  time: ChamberTime,
  centre: THREE.Vector3,
  _accent: THREE.Color,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'chamber-atmosphere';
  // (A rising column of light stood over the table here; it cost ~10 ms a frame of fill
  // and is not in the reference design, so the motes alone carry the atmosphere.)
  // Motes.
  let seed = 7;
  const rand = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const count = 380;
  const pos = new Float32Array(count * 3);
  const seeds = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    const a = rand() * Math.PI * 2;
    const r = Math.sqrt(rand()) * 9.5;
    pos.set([centre.x + Math.cos(a) * r, rand() * 5.4, centre.z + Math.sin(a) * r * 0.85], i * 3);
    seeds[i] = rand();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(centre.x, 3, centre.z), 14);
  const motes = new THREE.Points(
    g,
    new THREE.ShaderMaterial({
      uniforms: { uTime: time },
      vertexShader: /* glsl */ `
        uniform float uTime;
        attribute float aSeed;
        varying float vA;
        void main() {
          vec3 p = position;
          p.y = mod(p.y + uTime * (0.03 + aSeed * 0.05), 5.4);
          p.x += sin(uTime * 0.11 + aSeed * 40.0) * 0.4;
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          vA = smoothstep(0.0, 0.6, p.y) * (1.0 - smoothstep(4.4, 5.4, p.y)) * (0.4 + 0.6 * sin(uTime * 0.8 + aSeed * 30.0));
          gl_PointSize = (0.03 + aSeed * 0.03) * 650.0 / -mv.z;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        varying float vA;
        void main() {
          vec2 q = gl_PointCoord - 0.5;
          float a = max(0.0, 1.0 - dot(q, q) * 4.0) * max(vA, 0.0) * 0.7;
          gl_FragColor = vec4(vec3(1.0, 0.86, 0.62) * a, a);
        }`,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  motes.name = 'chamber-motes';
  group.add(motes);
  return group;
}
